import type { Vector3 } from '@babylonjs/core';
import type { PlayerId, PlayerIdentity, RgbColor } from '@roadworld/shared';

export interface PlayerListEntry {
  identity: PlayerIdentity;
  /** World position, or null if no snapshot yet. */
  pos: Vector3 | null;
}

export interface PlayerListProvider {
  myPosition(): Vector3;
  entries(): PlayerListEntry[];
}

const UPDATE_INTERVAL_MS = 200;
const COLLAPSE_KEY = 'roadworld.playerListCollapsed';

/**
 * DOM panel listing connected players. Click a row to invoke `onTeleport` with
 * the player's id. Collapses to a single "Players N" chip; click to expand.
 */
export class PlayerListPanel {
  private root: HTMLElement;
  private headerEl: HTMLElement;
  private titleEl: HTMLElement;
  private statusEl: HTMLElement;
  private bodyEl: HTMLElement;
  private listEl: HTMLElement;
  private provider: PlayerListProvider;
  private onTeleport: (id: PlayerId) => void;
  private lastUpdate = 0;
  private status: 'connecting' | 'connected' | 'disconnected' = 'connecting';
  private selfIdentity: PlayerIdentity | null = null;
  private pendingTeleportId: PlayerId | null = null;
  private collapsed = true;

  constructor(provider: PlayerListProvider, onTeleport: (id: PlayerId) => void) {
    this.provider = provider;
    this.onTeleport = onTeleport;

    // Initial collapsed state — persist via localStorage so the user's preference sticks.
    try {
      const v = localStorage.getItem(COLLAPSE_KEY);
      if (v !== null) this.collapsed = v === '1';
    } catch {
      // ignore
    }

    this.root = ensureNode('mpPlayerList', () => {
      const div = document.createElement('div');
      div.id = 'mpPlayerList';
      div.className = 'mp-player-list';
      div.innerHTML = `
        <button type="button" class="mp-pl-header" id="mpPlHeader" aria-expanded="false">
          <span class="mp-pl-title" id="mpPlTitle">Players 1</span>
          <span class="mp-pl-chev" id="mpPlChev">▾</span>
          <span class="mp-pl-status" id="mpPlStatus">connecting…</span>
        </button>
        <div class="mp-pl-body" id="mpPlBody">
          <ul class="mp-pl-rows" id="mpPlRows"></ul>
        </div>
      `;
      // Slot under the minimap card so it stacks below the zoom row + settings popover
      // (instead of using a hardcoded `top:` offset that breaks when the minimap grows).
      const slot = document.querySelector('.hud-top-right') ?? document.body;
      slot.appendChild(div);
      return div;
    });
    this.headerEl = this.root.querySelector('#mpPlHeader') as HTMLElement;
    this.titleEl = this.root.querySelector('#mpPlTitle') as HTMLElement;
    this.statusEl = this.root.querySelector('#mpPlStatus') as HTMLElement;
    this.bodyEl = this.root.querySelector('#mpPlBody') as HTMLElement;
    this.listEl = this.root.querySelector('#mpPlRows') as HTMLElement;

    this.headerEl.addEventListener('click', () => this.toggleCollapsed());
    this.applyCollapsed();
  }

  private toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
    try {
      localStorage.setItem(COLLAPSE_KEY, this.collapsed ? '1' : '0');
    } catch {
      // ignore
    }
    this.applyCollapsed();
  }

  private applyCollapsed(): void {
    this.root.classList.toggle('collapsed', this.collapsed);
    this.headerEl.setAttribute('aria-expanded', this.collapsed ? 'false' : 'true');
    this.bodyEl.hidden = this.collapsed;
  }

  setSelf(identity: PlayerIdentity): void {
    this.selfIdentity = identity;
    this.scheduleRender(true);
  }

  /** Show a loading/cancel state on the button for `id`, or clear it with null. */
  setPendingTeleport(id: PlayerId | null): void {
    this.pendingTeleportId = id;
    this.scheduleRender(true);
  }

  setStatus(status: 'connecting' | 'connected' | 'disconnected'): void {
    this.status = status;
    this.statusEl.textContent = status === 'connected' ? '●' : status;
    this.statusEl.className = `mp-pl-status mp-pl-status-${status}`;
  }

  update(now: number): void {
    if (now - this.lastUpdate < UPDATE_INTERVAL_MS) return;
    this.lastUpdate = now;
    this.render();
  }

  private scheduleRender(_force: boolean): void {
    this.lastUpdate = 0;
  }

  private render(): void {
    const me = this.provider.myPosition();
    const entries = this.provider.entries();

    // Header title: "Players N" where N includes self + others.
    const total = (this.selfIdentity ? 1 : 0) + entries.length;
    this.titleEl.textContent = `Players ${total}`;

    // Only render the list when expanded — saves work.
    if (this.collapsed) return;

    this.listEl.innerHTML = '';
    if (this.selfIdentity) {
      this.listEl.appendChild(buildRow(this.selfIdentity, 0, true, false, () => {}));
    }
    for (const e of entries) {
      const dist = e.pos ? Math.hypot(e.pos.x - me.x, e.pos.z - me.z) : null;
      const isPending = e.identity.id === this.pendingTeleportId;
      this.listEl.appendChild(
        buildRow(e.identity, dist, false, isPending, () => this.onTeleport(e.identity.id)),
      );
    }
    if (entries.length === 0 && this.selfIdentity) {
      const empty = document.createElement('li');
      empty.className = 'mp-pl-empty';
      empty.textContent = this.status === 'connected' ? 'No other players' : '—';
      this.listEl.appendChild(empty);
    }
  }
}

function buildRow(
  identity: PlayerIdentity,
  distance: number | null,
  isSelf: boolean,
  isPending: boolean,
  onClick: () => void,
): HTMLLIElement {
  const li = document.createElement('li');
  li.className = 'mp-pl-row' + (isSelf ? ' mp-pl-self' : '') + (isPending ? ' mp-pl-pending' : '');

  const swatch = document.createElement('span');
  swatch.className = 'mp-pl-swatch';
  swatch.style.backgroundColor = rgbToCss(identity.color);

  const name = document.createElement('span');
  name.className = 'mp-pl-name';
  name.textContent = identity.name + (isSelf ? ' (you)' : '');

  const meta = document.createElement('span');
  meta.className = 'mp-pl-meta';
  if (isSelf) {
    meta.textContent = '';
  } else if (isPending) {
    meta.textContent = 'loading…';
  } else if (distance === null) {
    meta.textContent = '…';
  } else {
    meta.textContent = formatDistance(distance);
  }

  li.appendChild(swatch);
  li.appendChild(name);
  li.appendChild(meta);

  if (!isSelf) {
    const btn = document.createElement('button');
    btn.className = isPending ? 'mp-pl-tp mp-pl-cancel' : 'mp-pl-tp';
    btn.textContent = isPending ? 'Cancel' : 'Teleport';
    btn.addEventListener('click', onClick);
    li.appendChild(btn);
  }
  return li;
}

function rgbToCss(c: RgbColor): string {
  const r = Math.round(c.r * 255);
  const g = Math.round(c.g * 255);
  const b = Math.round(c.b * 255);
  return `rgb(${r}, ${g}, ${b})`;
}

function formatDistance(m: number): string {
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

function ensureNode(id: string, create: () => HTMLElement): HTMLElement {
  const existing = document.getElementById(id);
  if (existing) return existing;
  return create();
}
